// Shared review_queue blocker helpers (migration 0013 /
// docs/design/hub-general-questions.md). Subject strings are the dedupe key
// for review_queue_pending_blocker_uidx — keep call sites in lockstep.
//
// Blocker-exit truth (fix-r17d): subject constructors AND the exit declaration
// live here. Portal exit copy and Worker clear sites both read
// `blocker_exit_declaration.json`. The drift lint
// (`scripts/blocker_exit_drift_lint.py`) fails when a clearer exists for a
// family the declaration calls attention, or the declaration claims a clearer
// nothing implements — and fails when the JSON is missing or unparseable.

import declarationJson from "./blocker_exit_declaration.json";

export function addBusinessDays(isoDate: string, businessDays: number): string {
  // Fixed calendar+N with Sat/Sun skip (no holiday calendar). Owner policy:
  // due_date = now + 3 business days.
  const date = new Date(`${isoDate.slice(0, 10)}T12:00:00.000Z`);
  let remaining = businessDays;
  while (remaining > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    const day = date.getUTCDay(); // 0=Sun … 6=Sat
    if (day !== 0 && day !== 6) {
      remaining -= 1;
    }
  }
  return date.toISOString().slice(0, 10);
}

export function formatPeriodLabel(period: string): string {
  // period is YYYY-MM; render "July 2026" for plain-English blocker copy.
  const year = Number.parseInt(period.slice(0, 4), 10);
  const month = Number.parseInt(period.slice(5, 7), 10);
  const labels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthLabel = labels[month - 1] ?? period;
  return `${monthLabel} ${String(year)}`;
}

export function closeBlockerSubject(period: string): string {
  return `Month-close blocked — ${formatPeriodLabel(period)}`.slice(0, 200);
}

/**
 * Period-scoped close-blocked subjects cleared when close for that period
 * succeeds. Unit of decision is (company, period) — not row id.
 *
 * Intentionally excludes closed-period-write, missing-account_id, and
 * unmapped-account subjects — those have their own named terminal writers
 * (see blocker_exit_declaration.json).
 */
export function closeLifecycleSubjectsForPeriod(period: string): [string, string] {
  return [closeBlockerSubject(period), periodReopenedBlockerSubject(period)];
}

export function closedPeriodWriteBlockerSubject(
  period: string,
  source: string,
): string {
  return `Closed-period write blocked — ${formatPeriodLabel(period)} (${source})`.slice(
    0,
    200,
  );
}

/** Prefix shared by every closed-period-write subject for a period (any source). */
export function closedPeriodWriteBlockerSubjectPrefix(period: string): string {
  return `Closed-period write blocked — ${formatPeriodLabel(period)}`;
}

export function periodReopenedBlockerSubject(period: string): string {
  return `Period reopened — re-close required — ${formatPeriodLabel(period)}`.slice(
    0,
    200,
  );
}

export function missingAccountIdBlockerSubject(txnId: string): string {
  return `Missing Plaid account_id — ${txnId}`.slice(0, 200);
}

/**
 * Account id used in unmapped subjects — MUST match unmappedAccountSource
 * truncation (source max 64 = prefix + id). Raise and clear both go through
 * this so a long Plaid id cannot strand a row (F5).
 */
export function unmappedAccountIdForSubject(accountId: string): string {
  const source = unmappedAccountSource(accountId);
  return source.slice(UNMAPPED_ACCOUNT_SOURCE_PREFIX.length) || "(missing)";
}

export function unmappedAccountBlockerSubject(accountId: string): string {
  return `Unmapped Plaid account — ${unmappedAccountIdForSubject(accountId)}`.slice(
    0,
    200,
  );
}

/**
 * close-todos kind=write-blocked with this source prefix derives an
 * unmapped-account subject from the remainder (account id). Keeps per-account
 * dedupe without a caller-supplied subject override (M2).
 */
export const UNMAPPED_ACCOUNT_SOURCE_PREFIX = "unmapped:" as const;

export function unmappedAccountSource(accountId: string): string {
  return `${UNMAPPED_ACCOUNT_SOURCE_PREFIX}${accountId}`.slice(0, 64);
}

export function statementBlockerSubject(period: string, account: string): string {
  // Deterministic dedupe key for review_queue_pending_blocker_uidx
  // (company, reason, subject). Account path kept short for the 200-char cap.
  const shortAccount = account.length > 80 ? `${account.slice(0, 77)}...` : account;
  return `Statement reconciliation blocked — ${shortAccount}, ${formatPeriodLabel(period)}`.slice(
    0,
    200,
  );
}

/**
 * SHA-keyed stale-publication card. Distinct from statementBlockerSubject so
 * statement confirm cannot approve the published-A trail (SR-07).
 * Unique index unit is (company, reason, subject); subject includes the
 * published ledger SHA so retries of the same commit do not duplicate.
 */
export const STALE_PUBLICATION_SUBJECT_PREFIX =
  "Stale published assertion — " as const;

export function stalePublicationBlockerSubject(
  period: string,
  account: string,
  publishedLedgerSha: string,
): string {
  const sha = publishedLedgerSha.slice(0, 40);
  const head = `${STALE_PUBLICATION_SUBJECT_PREFIX}${sha}`;
  const rest = ` — ${account}, ${formatPeriodLabel(period)}`;
  const budget = 200 - head.length;
  if (budget <= 0) {
    return head.slice(0, 200);
  }
  if (rest.length <= budget) {
    return `${head}${rest}`;
  }
  return `${head}${rest.slice(0, budget)}`;
}

export function isStalePublicationBlockerSubject(subject: string): boolean {
  return subject.startsWith(STALE_PUBLICATION_SUBJECT_PREFIX);
}

export type BlockerQueueReason =
  | "close-blocked"
  | "statement-blocked"
  | "escrow-balance";

/** Blocker-shaped review_queue.reason values (shape partition, not a second grant). */
export function isBlockerQueueReason(reason: string): reason is BlockerQueueReason {
  return (
    reason === "close-blocked" ||
    reason === "statement-blocked" ||
    reason === "escrow-balance"
  );
}

/**
 * GRANT (fix-r36f) escrow-balance AM card. Dedupe key is
 * (company, reason, subject) — period in the subject so one card per loan
 * per period, not an infinite nag on one row.
 *
 * R37A-3: the unique-index unit is the loan, not a 77-character prefix.
 * Display may truncate; the fingerprint is FNV-1a 64 of the *full* name
 * (identity, not a MAC — this route is already fleet-key).
 */
export const ESCROW_BALANCE_SUBJECT_PREFIX = "Escrow balance — " as const;

export function escrowBalanceLoanFingerprint(loanName: string): string {
  let hash = 0xcbf29ce484222325n;
  const bytes = new TextEncoder().encode(loanName);
  for (const byte of bytes) {
    hash ^= BigInt(byte);
    hash = (hash * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return hash.toString(16).padStart(16, "0");
}

export function escrowBalanceBlockerSubject(
  period: string,
  loanName: string,
): string {
  const fingerprint = escrowBalanceLoanFingerprint(loanName);
  const display =
    loanName.length > 80 ? `${loanName.slice(0, 77)}...` : loanName;
  return `${ESCROW_BALANCE_SUBJECT_PREFIX}${display} · ${fingerprint}, ${formatPeriodLabel(period)}`.slice(
    0,
    200,
  );
}

/**
 * Receipt match-conflict AM to-do (W1). Uses close-blocked blocker shape —
 * same non-transaction vehicle as unmapped-account / missing-account_id —
 * without a new reason enum (frozen contract). Subject is the dedupe key;
 * not a close-lifecycle subject (admin portal neutral exit).
 *
 * Correction/unmatch action is escalated — demoteMatchedForTxn needs an
 * authorised human-triggered caller; do not add that endpoint in this lane.
 */
export const RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX =
  "Receipt match conflict — " as const;

export function receiptMatchConflictSubject(plaidTransactionId: string): string {
  return `${RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX}${plaidTransactionId}`.slice(
    0,
    200,
  );
}

export function plaidTxnIdFromReceiptMatchConflictSubject(
  subject: string,
): string | null {
  if (!subject.startsWith(RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX)) {
    return null;
  }
  const id = subject.slice(RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX.length).trim();
  return id.length > 0 ? id : null;
}

/**
 * Single authority for close-todos subjects (M2). Derived from kind + period
 * (+ source for write-blocked / unmapped). Callers do not supply subject.
 */
export function derivedCloseTodoSubject(
  kind: "write-blocked" | "reopened",
  period: string,
  source: string,
): string {
  if (kind === "reopened") {
    return periodReopenedBlockerSubject(period);
  }
  if (source.startsWith(UNMAPPED_ACCOUNT_SOURCE_PREFIX)) {
    const accountId =
      source.slice(UNMAPPED_ACCOUNT_SOURCE_PREFIX.length) || "(missing)";
    return unmappedAccountBlockerSubject(accountId);
  }
  return closedPeriodWriteBlockerSubject(period, source);
}

// ---------------------------------------------------------------------------
// Blocker-exit declaration (fix-r17d) — one source for clearers + AM copy
// ---------------------------------------------------------------------------

export type BlockerClearMode = "system" | "am-disposition" | "conditional" | "attention";

/**
 * Where the action that clears a card can be taken (rd round 12, A3-5).
 * `ledger-only` means a person must act and cannot do it in the console; the
 * drift lint requires the "by hand for now" sentence on those families.
 * `month-end-close` is the one route that is neither the console nor a ledger
 * edit: the month-end close run closes the month again.
 */
export type BlockerClearingAction =
  | "automatic"
  | "console"
  | "month-end-close"
  | "ledger-only";

export type BlockerExitFamilyId =
  | "month-close"
  | "period-reopened"
  | "closed-period-write"
  | "missing-account-id"
  | "unmapped-account"
  | "receipt-match-conflict"
  | "hard-match-refuse"
  | "receipt-match-review"
  | "unmatched-hub-gl"
  | "hub-gl-push-refused"
  | "statement"
  | "stale-publication"
  | "escrow-balance";

export type BlockerExitFamily = {
  id: BlockerExitFamilyId;
  reason: "close-blocked" | "statement-blocked" | "escrow-balance";
  subjectPrefix: string;
  clearMode: BlockerClearMode;
  /** Where the clearing action can be taken (drift-checked against the JSON). */
  clearingAction: BlockerClearingAction;
  resolvedBy: string | null;
  writer: string | null;
  writerFile: string | null;
  exitKind: string;
  /**
   * The legacy single sentence. The portal still reads it until wave B (B1)
   * switches to the role sentences; keep it byte-identical for now.
   */
  exitSentence: string;
  /** What the owner reads for this family (rd round 12, decision 2/4/12). */
  ownerSentence: string;
  /** What the account manager reads for this family. */
  amSentence: string;
  escalatePlaceholder: string;
  /** Optional declaration note — not AM-visible. */
  comment?: string;
};

type DeclarationFile = {
  version: number;
  attentionExitSentence: string;
  attentionOwnerSentence: string;
  attentionAmSentence: string;
  attentionEscalatePlaceholder: string;
  families: BlockerExitFamily[];
};

const declaration = declarationJson as DeclarationFile;

/** Runtime companion of `BlockerClearingAction` for the load-time check. */
const BLOCKER_CLEARING_ACTIONS: readonly string[] = [
  "automatic",
  "console",
  "month-end-close",
  "ledger-only",
];

/** Fail-closed: empty/unparseable declaration is a load-time error. */
if (
  !Number.isFinite(declaration.version) ||
  declaration.version < 1 ||
  !Array.isArray(declaration.families) ||
  declaration.families.length === 0 ||
  typeof declaration.attentionExitSentence !== "string" ||
  declaration.attentionExitSentence.length === 0 ||
  typeof declaration.attentionOwnerSentence !== "string" ||
  declaration.attentionOwnerSentence.length === 0 ||
  typeof declaration.attentionAmSentence !== "string" ||
  declaration.attentionAmSentence.length === 0 ||
  declaration.families.some(
    (family) =>
      typeof family.ownerSentence !== "string" ||
      family.ownerSentence.length === 0 ||
      typeof family.amSentence !== "string" ||
      family.amSentence.length === 0 ||
      !BLOCKER_CLEARING_ACTIONS.includes(family.clearingAction),
  )
) {
  throw new Error(
    "blocker_exit_declaration.json missing, empty, or unparseable (fix-r17d)",
  );
}

export const BLOCKER_EXIT_DECLARATION: DeclarationFile = declaration;

export const ATTENTION_EXIT_SENTENCE = declaration.attentionExitSentence;
export const ATTENTION_ESCALATE_PLACEHOLDER =
  declaration.attentionEscalatePlaceholder;

/**
 * R13O-2 / R14O-1: tipped hard-match refuse subject prefix + keep-bank note.
 * Lived in receipt_context; moved here so the exit declaration and subject
 * constructors share one module (portal + worker both import this file).
 */
export const RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX =
  "Receipt hard-match refused — " as const;

export const HARD_MATCH_REFUSE_KEEP_BANK_ESCALATION_PREFIX =
  "keep-bank:" as const;

export const HARD_MATCH_REFUSE_KEEP_BANK_RESOLUTION =
  "hard-match-refuse:keep-bank" as const;

export function receiptHardMatchRefusedSubject(
  plaidTransactionId: string,
): string {
  return `${RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX}${plaidTransactionId}`.slice(
    0,
    200,
  );
}

export function plaidTxnIdFromHardMatchRefusedSubject(
  subject: string,
): string | null {
  if (!subject.startsWith(RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX)) {
    return null;
  }
  const id = subject
    .slice(RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX.length)
    .trim();
  return id.length > 0 ? id : null;
}

/** System clearer resolvedBy for a family id — throws if not a system family. */
export function systemResolvedByForFamily(familyId: BlockerExitFamilyId): string {
  const family = declaration.families.find((f) => f.id === familyId);
  if (
    family === undefined ||
    family.clearMode !== "system" ||
    family.resolvedBy === null
  ) {
    throw new Error(
      `blocker exit family ${familyId} has no system resolvedBy in declaration`,
    );
  }
  return family.resolvedBy;
}

/**
 * Resolve the declared exit for a queue item. Named families only; unknown
 * subjects fall to attention (the check that would have caught R12O-6 /
 * R14O-4 / R16O-2). Exact constructor equality for lifecycle / conflict /
 * refuse — prefix alone is not enough (N1).
 */
export function resolveBlockerExitFamily(item: {
  reason: string;
  subject?: string | undefined;
}): BlockerExitFamily | null {
  if (item.reason === "statement-blocked") {
    if (
      item.subject !== undefined &&
      isStalePublicationBlockerSubject(item.subject)
    ) {
      return declaration.families.find((f) => f.id === "stale-publication") ?? null;
    }
    return declaration.families.find((f) => f.id === "statement") ?? null;
  }
  if (item.reason === "escrow-balance") {
    return declaration.families.find((f) => f.id === "escrow-balance") ?? null;
  }
  if (item.reason !== "close-blocked") return null;
  const subject = item.subject;
  if (subject === undefined || subject.length === 0) return null;

  if (isCloseLifecycleAutoClearSubject(subject)) {
    if (subject.startsWith("Month-close blocked — ")) {
      return declaration.families.find((f) => f.id === "month-close") ?? null;
    }
    return declaration.families.find((f) => f.id === "period-reopened") ?? null;
  }
  if (isClosedPeriodWriteBlockerSubject(subject)) {
    return (
      declaration.families.find((f) => f.id === "closed-period-write") ?? null
    );
  }
  if (isMissingAccountIdBlockerSubject(subject)) {
    return (
      declaration.families.find((f) => f.id === "missing-account-id") ?? null
    );
  }
  if (isUnmappedAccountBlockerSubject(subject)) {
    return declaration.families.find((f) => f.id === "unmapped-account") ?? null;
  }
  if (isReceiptMatchConflictSubjectExact(subject)) {
    return (
      declaration.families.find((f) => f.id === "receipt-match-conflict") ?? null
    );
  }
  if (isHardMatchRefusedSubjectExact(subject)) {
    return (
      declaration.families.find((f) => f.id === "hard-match-refuse") ?? null
    );
  }
  if (isReceiptMatchReviewSubjectExact(subject)) {
    return (
      declaration.families.find((f) => f.id === "receipt-match-review") ?? null
    );
  }
  if (isUnmatchedHubGlSubjectExact(subject)) {
    return (
      declaration.families.find((f) => f.id === "unmatched-hub-gl") ?? null
    );
  }
  if (isHubGlPushRefusedSubjectExact(subject)) {
    return (
      declaration.families.find((f) => f.id === "hub-gl-push-refused") ?? null
    );
  }
  return null;
}

export function isCloseLifecycleAutoClearSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const candidates: Array<{ prefix: string; build: (p: string) => string }> = [
    { prefix: "Month-close blocked — ", build: closeBlockerSubject },
    {
      prefix: "Period reopened — re-close required — ",
      build: periodReopenedBlockerSubject,
    },
  ];
  for (const { prefix, build } of candidates) {
    if (!subject.startsWith(prefix)) continue;
    const period = periodFromCloseLabel(subject.slice(prefix.length));
    if (period === null) continue;
    if (subject === build(period)) return true;
  }
  return false;
}

function periodFromCloseLabel(label: string): string | null {
  const match =
    /^(January|February|March|April|May|June|July|August|September|October|November|December) (\d{4})$/.exec(
      label,
    );
  if (match === null) return null;
  const monthName = match[1];
  const year = match[2];
  if (monthName === undefined || year === undefined) return null;
  const labels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = labels.indexOf(monthName);
  if (month < 0) return null;
  return `${year}-${String(month + 1).padStart(2, "0")}`;
}

export function isClosedPeriodWriteBlockerSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const prefix = "Closed-period write blocked — ";
  if (!subject.startsWith(prefix)) return false;
  // Exact constructor shape: label (source)
  const rest = subject.slice(prefix.length);
  const m =
    /^(January|February|March|April|May|June|July|August|September|October|November|December) (\d{4}) \((.+)\)$/.exec(
      rest,
    );
  if (m === null) return false;
  const monthName = m[1];
  const year = m[2];
  const source = m[3];
  if (
    monthName === undefined ||
    year === undefined ||
    source === undefined ||
    source.length === 0
  ) {
    return false;
  }
  const labels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = labels.indexOf(monthName);
  if (month < 0) return false;
  const period = `${year}-${String(month + 1).padStart(2, "0")}`;
  return subject === closedPeriodWriteBlockerSubject(period, source);
}

export function isMissingAccountIdBlockerSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const prefix = "Missing Plaid account_id — ";
  if (!subject.startsWith(prefix)) return false;
  const id = subject.slice(prefix.length).trim();
  if (id.length === 0) return false;
  return subject === missingAccountIdBlockerSubject(id);
}

export function isUnmappedAccountBlockerSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const prefix = "Unmapped Plaid account — ";
  if (!subject.startsWith(prefix)) return false;
  const id = subject.slice(prefix.length).trim();
  if (id.length === 0) return false;
  // Reconstruct via unmappedAccountIdForSubject path (truncation-aware).
  return subject === unmappedAccountBlockerSubject(id);
}

function isReceiptMatchConflictSubjectExact(subject: string): boolean {
  if (!subject.startsWith(RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX)) return false;
  const id = subject.slice(RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX.length).trim();
  if (id.length === 0) return false;
  return subject === receiptMatchConflictSubject(id);
}

function isHardMatchRefusedSubjectExact(subject: string): boolean {
  if (!subject.startsWith(RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX)) {
    return false;
  }
  const id = subject
    .slice(RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX.length)
    .trim();
  if (id.length === 0) return false;
  return subject === receiptHardMatchRefusedSubject(id);
}

/**
 * Proposed-match AM review (feat-receipt-missing-shelf). match_verified=false
 * attach as context; AM confirms, rejects (txn returns to the missing shelf),
 * or escalates via escalateQueueItem. Reuses close-blocked without a new
 * review_queue.reason (frozen).
 */
export const RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX =
  "Receipt match review — " as const;

export const RECEIPT_MATCH_REVIEW_ATTACH_PREFIX = "attach:" as const;
export const RECEIPT_MATCH_REVIEW_REJECT_PREFIX = "reject:" as const;

export function receiptMatchReviewSubject(plaidTransactionId: string): string {
  return `${RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX}${plaidTransactionId}`.slice(
    0,
    200,
  );
}

export function plaidTxnIdFromReceiptMatchReviewSubject(
  subject: string,
): string | null {
  if (!subject.startsWith(RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX)) {
    return null;
  }
  const id = subject.slice(RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX.length).trim();
  return id.length > 0 ? id : null;
}

export function isReceiptMatchReviewAttachNote(note: string): boolean {
  return note.trim().toLowerCase().startsWith(RECEIPT_MATCH_REVIEW_ATTACH_PREFIX);
}

export function isReceiptMatchReviewRejectNote(note: string): boolean {
  return note.trim().toLowerCase().startsWith(RECEIPT_MATCH_REVIEW_REJECT_PREFIX);
}

function isReceiptMatchReviewSubjectExact(subject: string): boolean {
  if (!subject.startsWith(RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX)) return false;
  const id = subject.slice(RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX.length).trim();
  if (id.length === 0) return false;
  return subject === receiptMatchReviewSubject(id);
}

export function isReceiptMatchReviewSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  return isReceiptMatchReviewSubjectExact(subject);
}

/**
 * Month-end unmatched hub GL batch (FAM-1 / DR-4). Reuses close-blocked;
 * subject is the dedupe key. Not a close-lifecycle subject.
 */
export const UNMATCHED_HUB_GL_SUBJECT_PREFIX = "Unmatched hub GL batch — " as const;

export function unmatchedHubGlBatchSubject(
  period: string,
  sourceBatchId: string,
): string {
  return `${UNMATCHED_HUB_GL_SUBJECT_PREFIX}${formatPeriodLabel(period)} (${sourceBatchId})`.slice(
    0,
    200,
  );
}

export function isUnmatchedHubGlSubjectExact(subject: string): boolean {
  if (!subject.startsWith(UNMATCHED_HUB_GL_SUBJECT_PREFIX)) return false;
  const rest = subject.slice(UNMATCHED_HUB_GL_SUBJECT_PREFIX.length);
  const labels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  // Recover the id by construction: everything after `Month YYYY (` up to
  // the final `)`. Do not use `.` — JS `.` does not match line terminators,
  // which is how R24R-2 made both R23O-3 directions fall to attention.
  const prefix = new RegExp(
    `^(${labels.join("|")}) (\\d{4}) \\(`,
  ).exec(rest);
  if (prefix === null || !rest.endsWith(")")) return false;
  const monthName = prefix[1];
  const year = prefix[2];
  if (monthName === undefined || year === undefined) return false;
  const sourceBatchId = rest.slice(prefix[0].length, -1);
  if (sourceBatchId.length === 0) return false;
  const month = labels.indexOf(monthName);
  if (month < 0) return false;
  const period = `${year}-${String(month + 1).padStart(2, "0")}`;
  return subject === unmatchedHubGlBatchSubject(period, sourceBatchId);
}

/**
 * Content-refusal AM card (#647 / R27O-4=A). Subject is the pending-blocker
 * dedupe key; period is YYYY-MM. Cleared when a later hub GL push for the
 * same (company, period) is accepted and stored — see declaration comment.
 */
export const HUB_GL_PUSH_REFUSED_SUBJECT_PREFIX =
  "Hub GL push refused — " as const;

export function hubGlPushRefusedSubject(period: string): string {
  return `${HUB_GL_PUSH_REFUSED_SUBJECT_PREFIX}${period}`.slice(0, 200);
}

export function isHubGlPushRefusedSubjectExact(subject: string): boolean {
  if (!subject.startsWith(HUB_GL_PUSH_REFUSED_SUBJECT_PREFIX)) return false;
  const period = subject.slice(HUB_GL_PUSH_REFUSED_SUBJECT_PREFIX.length);
  if (!/^[0-9]{4}-[0-9]{2}$/.test(period)) return false;
  return subject === hubGlPushRefusedSubject(period);
}
